import React from 'react';
import { Snackbar, Box, Typography, IconButton } from '@mui/material';
import { 
    Close, 
    WarningAmberOutlined, 
    CheckCircleOutline, 
    InfoOutlined, 
    ErrorOutline 
} from '@mui/icons-material';


const CommonAlert = ({ show, message, severity = 'warning', onClose }) => {
    // Dynamic configurations safety fallback selector
    const currentTheme = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.warning;

    return (
        <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            open={show}
            autoHideDuration={5000}
            onClose={onClose}
            sx={{ 
                bottom: { xs: 90, sm: 24 },
                right: { xs: 16, sm: 24 },
                maxWidth: '400px',
                width: 'calc(100% - 32px)'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.75,
                    p: 2,
                    width: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    border: `1px solid ${currentTheme.borderColor}`, // 💡 Dynamic Border
                    borderRadius: '14px',
                    boxShadow: '0 12px 32px -4px rgba(15, 23, 42, 0.08), 0 4px 12px -2px rgba(15, 23, 42, 0.04)',
                    position: 'relative',
                    overflow: 'hidden',
                    
                    // Left-edge minimal accent glow bar
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        backgroundColor: currentTheme.color, // 💡 Dynamic Left Bar Accent
                    }
                }}
            >
                {/* Dynamic Status Icon Container with Ambient Halo Animation */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        position: 'relative',
                        mt: '2px',
                        color: currentTheme.color, // Hooks primary vector color rules
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            backgroundColor: currentTheme.bgColor, // 💡 Dynamic Halo Color
                            animation: 'pulseHalo 2s infinite ease-in-out',
                            '@keyframes pulseHalo': {
                                '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                                '50%': { transform: 'scale(1.25)', opacity: 0.4 }
                            }
                        }
                    }}
                >
                    {currentTheme.icon}
                </Box>

                {/* Typography Text Section */}
                <Box sx={{ flexGrow: 1, minWidth: 0, pr: 2, textAlign: 'left' }}>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            display: 'block', 
                            fontWeight: 700, 
                            color: currentTheme.textColor, // 💡 Dynamic Title Color
                            textTransform: 'uppercase', 
                            letterSpacing: '0.05em',
                            fontSize: '0.65rem',
                            mb: 0.5
                        }}
                    >
                        {currentTheme.title}
                    </Typography>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: '#334155', 
                            fontWeight: 500, 
                            fontSize: '0.8rem', 
                            lineHeight: 1.4,
                            wordBreak: 'break-word'
                        }}
                    >
                        {message}
                    </Typography>
                </Box>

                {/* Minimalist Close Trigger button */}
                <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{
                        p: '4px',
                        color: '#94a3b8',
                        borderRadius: '6px',
                        mt: '-2px',
                        marginRight: '-4px',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                            backgroundColor: 'rgba(15, 23, 42, 0.05)',
                            color: '#334155'
                        }
                    }}
                >
                    <Close sx={{ fontSize: '0.95rem' }} />
                </IconButton>
            </Box>
        </Snackbar>
    );
};

export default CommonAlert;

// 💡 1. Config Object Mapping Themes and Icons for each severity param
const SEVERITY_CONFIG = {
    success: {
        title: "Success",
        icon: <CheckCircleOutline sx={{ fontSize: '1.25rem', zIndex: 1 }} />,
        color: '#16a34a',       // Primary green
        textColor: '#15803d',   // Deep text green
        borderColor: 'rgba(22, 163, 74, 0.25)',
        bgColor: 'rgba(22, 163, 74, 0.1)'
    },
    warning: {
        title: "Attention Required",
        icon: <WarningAmberOutlined sx={{ fontSize: '1.25rem', zIndex: 1 }} />,
        color: '#f59e0b',       // Primary amber
        textColor: '#b45309',   // Deep text amber
        borderColor: 'rgba(245, 158, 11, 0.25)',
        bgColor: 'rgba(245, 158, 11, 0.1)'
    },
    error: {
        title: "Error Encountered",
        icon: <ErrorOutline sx={{ fontSize: '1.25rem', zIndex: 1 }} />,
        color: '#dc2626',       // Primary red
        textColor: '#b91c1c',   // Deep text red
        borderColor: 'rgba(220, 38, 38, 0.25)',
        bgColor: 'rgba(220, 38, 38, 0.1)'
    },
    info: {
        title: "Information Notice",
        icon: <InfoOutlined sx={{ fontSize: '1.25rem', zIndex: 1 }} />,
        color: '#6769DF',       // Your brand's signature violet
        textColor: '#4f46e5',   // Deep text violet
        borderColor: 'rgba(103, 105, 223, 0.25)',
        bgColor: 'rgba(103, 105, 223, 0.1)'
    }
};
